import { DEFAULT_CHECKIN_SETTINGS, type CheckinSettings } from '../checkins';
import type { PracticeArea } from '../intake';
import type { UpdateMode } from '../matters';
import { resolveRouting, type PaymentRouting } from '../payments';

/** Team roles. owner: everything; lawyer: client work + approvals; staff: client work, no approvals/settings. */
export const ROLES = ['owner', 'lawyer', 'staff'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = { owner: 'Owner', lawyer: 'Lawyer', staff: 'Staff' };

export type Permission = 'settings' | 'billing' | 'team' | 'approve_updates' | 'conflict_decision' | 'client_work' | 'audit' | 'payments_admin';

const MATRIX: Record<Role, Permission[]> = {
  owner: ['settings', 'billing', 'team', 'approve_updates', 'conflict_decision', 'client_work', 'audit', 'payments_admin'],
  lawyer: ['approve_updates', 'conflict_decision', 'client_work', 'audit'],
  staff: ['client_work'],
};

export function can(role: string | null | undefined, permission: Permission): boolean {
  return !!role && (MATRIX[role as Role] ?? []).includes(permission);
}

/** Hive role → LawBee role (contract: owner→owner, admin→admin (owner here), member→default staff role). */
export function roleFromHive(role: string): Role {
  if (role === 'owner' || role === 'admin') return 'owner';
  return 'staff';
}

export type DataRegion = 'ca' | 'us';

export type WorkspaceSettings = {
  practiceAreas: PracticeArea[];
  /** AI features run only when the firm turns them on. */
  aiEnabled: boolean;
  updateMode: UpdateMode;
  checkins: CheckinSettings;
  paymentRouting: PaymentRouting;
  dataRegion: DataRegion;
  confidentialityNotice: string;
  /** Integrations email fallback: new intakes and sent updates are copied to this address. */
  emailFallbackAddress: string | null;
  clio: { enabled: boolean; connectedAt: string | null };
  onboardingCompleted: boolean;
  onboardingStep: number;
  phone: string | null;
  address: string | null;
  replyToEmail: string | null;
};

export const DEFAULT_CONFIDENTIALITY_NOTICE =
  'Information you share with us is kept confidential. Submitting this form or booking a consultation does not by itself create a lawyer-client relationship. Do not send information you consider highly sensitive until a lawyer has agreed to act for you.';

export function resolveSettings(raw: Partial<WorkspaceSettings> | null | undefined): WorkspaceSettings {
  const r = raw ?? {};
  return {
    practiceAreas: Array.isArray(r.practiceAreas) ? r.practiceAreas : [],
    aiEnabled: r.aiEnabled === true,
    updateMode: r.updateMode === 'auto' ? 'auto' : 'approve',
    checkins: { ...DEFAULT_CHECKIN_SETTINGS, ...(r.checkins ?? {}) },
    paymentRouting: resolveRouting(r.paymentRouting),
    dataRegion: r.dataRegion === 'us' ? 'us' : 'ca',
    confidentialityNotice: r.confidentialityNotice?.trim() || DEFAULT_CONFIDENTIALITY_NOTICE,
    emailFallbackAddress: r.emailFallbackAddress ?? null,
    clio: { enabled: r.clio?.enabled === true, connectedAt: r.clio?.connectedAt ?? null },
    onboardingCompleted: r.onboardingCompleted === true,
    onboardingStep: typeof r.onboardingStep === 'number' ? r.onboardingStep : 0,
    phone: r.phone ?? null,
    address: r.address ?? null,
    replyToEmail: r.replyToEmail ?? null,
  };
}

export const DATA_REGION_NOTES: Record<DataRegion, string> = {
  ca: 'Primary data is stored in Cloudflare D1 and R2 with a Canada/North America location hint. Cloudflare may process requests at edge locations worldwide.',
  us: 'Primary data is stored in Cloudflare D1 and R2 with a North America location hint.',
};

export const AI_PRIVACY_NOTE =
  'AI features are off unless your firm turns them on. When on, only the text needed for the task is sent to Anthropic\'s API, which does not use API data to train its models. LawBee never uses client data for training.';

export function slugify(name: string): string {
  return (
    name
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'firm'
  );
}
